import { getResourceQueryKey, ResourceType } from '../../resources';

/**
 * Maximum length for reason strings in modals
 */
const MAX_REASON_LENGTH = 1000;

/**
 * Validates and sanitizes a reason string
 * @param reason - The reason string to validate
 * @returns Sanitized reason string or throws error if invalid
 */
export const validateReason = (reason: string): string => {
	const trimmed = reason.trim();
	if (trimmed.length > MAX_REASON_LENGTH) {
		throw new Error(`Reason must be ${MAX_REASON_LENGTH} characters or less`);
	}
	return trimmed;
};

/**
 * Validates a ticket ID format to prevent path injection
 * @param ticketId - The ticket ID to validate
 * @returns true if valid, false otherwise
 */
export const isValidTicketId = (ticketId: string): boolean => {
	return typeof ticketId === 'string' && ticketId.length > 0;
};

/**
 * Validates a ticket ID and throws if invalid
 * @param ticketId - The ticket ID to validate
 * @throws Error if ticket ID is invalid
 */
export const validateTicketId = (ticketId: string): void => {
	if (!isValidTicketId(ticketId)) {
		throw new Error('Invalid ticket ID format');
	}
};

/**
 * Validates an array of ticket IDs and throws if any are invalid
 * @param ticketIds - Array of ticket IDs to validate
 * @throws Error if any ticket ID is invalid or array is empty
 */
export const validateTicketIds = (ticketIds: string[]): void => {
	if (!Array.isArray(ticketIds) || ticketIds.length === 0) {
		throw new Error('At least one ticket ID is required');
	}
	ticketIds.forEach((ticketId, index) => {
		if (!isValidTicketId(ticketId)) {
			throw new Error(`Invalid ticket ID format at index ${index}: ${ticketId}`);
		}
	});
};

export const getEndpointForResourceType = (resourceType: ResourceType) => {
	switch (resourceType) {
		case 'checkouts':
			return 'orders';
		default:
			return resourceType;
	}
};

export const getTicketReferencesQueryKey = (ticketId: string) => {
	return getResourceQueryKey({ resourceId: ticketId }, ResourceType.TICKETS, 'ticket-references');
};
